import {
  createFileRoute,
  Outlet,
  type ErrorComponentProps,
} from '@tanstack/react-router'
import type { ReactNode } from 'react'

import { ACCOUNT } from '@brickup/shared/locales/de/account'
import { AppFrame } from '@/components/AppFrame'
import { ReadOnlyScope } from '@/components/base'
import { DefaultErrorFallback } from '@/components/ErrorBoundary'
import { AccessDenied } from '@/components/Internal/AccessDenied'
import { PageLayout } from '@/components/Internal/PageLayout'
import { SideNav } from '@/components/Internal/SideNav'
import { useBareChrome } from '@/hooks/use-bare-chrome'
import { useCanChangeArea } from '@/hooks/use-navigation-permissions'
import {
  AccessDeniedError,
  requireAuth,
  requireRoutePermission,
} from '@/utils/require-auth'

/** Everything staff-facing: the rail, and the screen beside it. */
export const Route = createFileRoute('/internal')({
  beforeLoad: async ({ context, location }) => {
    const { roles } = await requireAuth(context.queryClient, location.href)
    requireRoutePermission(roles, location.pathname)
  },
  component: InternalLayout,
  errorComponent: InternalRouteError,
})

/**
 * A refusal is shown inside the shell, not instead of it.
 *
 * `AccessDeniedError` is only thrown for somebody already signed in, so the
 * rail and the header are theirs to keep — and what they reach for next is on
 * that header: the areas their role does have, and the switcher root gets back
 * out of an assumed role with. Rendered bare, each of those would have to be
 * repeated on the refusal itself.
 */
function InternalRouteError({ error, reset }: ErrorComponentProps) {
  if (error instanceof AccessDeniedError) {
    return (
      <InternalLayout>
        <PageLayout title={ACCOUNT.accessDenied}>
          <AccessDenied />
        </PageLayout>
      </InternalLayout>
    )
  }
  return <DefaultErrorFallback error={error} onRetry={reset} />
}

/**
 * The rail and the screen beside it.
 *
 * An area the reader may open without working in it puts its screen in a
 * read-only scope, from the same table the rail and the router read — so a
 * role that only reads an area is refused by one answer rather than by each
 * screen remembering to ask. System's pages carry the narrower rule per
 * group and set their own scope inside this one.
 */
function InternalLayout({ children }: { children?: ReactNode }) {
  const canChange = useCanChangeArea()
  // A bare screen — the customer's configurator, opened as them — brings its
  // own full-height chrome and shows what the customer sees, which is none of
  // this.
  const body = (
    <ReadOnlyScope readOnly={!canChange}>
      {children ?? <Outlet />}
    </ReadOnlyScope>
  )
  if (useBareChrome()) return body

  return (
    <div className="flex min-h-0 flex-1 flex-row bg-ground text-[13px]">
      <SideNav />

      <AppFrame>{body}</AppFrame>
    </div>
  )
}
